import React from 'react'
import { createSelector } from 'reselect'
import { get } from 'lodash-es'
import { keys, map, pipe, uniqBy, flatten } from 'ramda'
import { format } from 'date-fns'
import { partyApiSelectors } from '../../api/party'
import { portfolioApiSelectors } from '../../api/portfolio'
import { hasLiveLease } from '../status'
import { NAMESPACE } from '../constants'

const getState = (state: any) => state

export const contactsState = createSelector(getState, s => get(s, [NAMESPACE]))

export const getSearchQuery = createSelector(contactsState, s => get(s, 'searchQuery'))

export const depositTransferModal = createSelector(contactsState, s => get(s, 'depositTransferModal'))

export const getPartyDataForContactsTable = createSelector(getState, state => (id: any) => {
  const partyType = partyApiSelectors.getPartyTypeById(state)(id)
  const name =
    partyType === 'person'
      ? partyApiSelectors.getPartyNameById(state)(id)
      : partyApiSelectors.getPartyTradingAsOrCompanyNameById(state)(id)
  const lastName = partyApiSelectors.getPartyLastNameById(state)(id)
  const email = partyApiSelectors.getPartyEmailById(state)(id)
  const contactNumber = partyApiSelectors.getPartyTelNumberById(state)(id)
  const idNumber = partyType === 'person' ? partyApiSelectors.getPersonsIdNumber(state)(id) : null
  const passportNumber = partyType === 'person' ? partyApiSelectors.getPersonsPassportNumber(state)(id) : null
  const companyRegistration = partyApiSelectors.getPartyCompanyRegistration(state)(id) || null
  // Not rendered — `name` already falls back to it — but carried so search still finds a legal
  // entity by its registered name when a trading name is displayed instead.
  const companyName = partyType === 'company' ? partyApiSelectors.getPartyCompanyNameById(state)(id) : undefined
  const createdAt = partyApiSelectors.getPartyPropertyById(state)(id, 'createdAt')
  const tags = partyApiSelectors.getPartyTagsById(state)(id)
  const accounts = partyApiSelectors.getPartyAccountsById(state)(id)
  const hasBankDetails = partyApiSelectors.partyHasBankDetails(state)(id)
  const ficaCompliance = partyApiSelectors.getPartyFicaCompliance(state)(id)
  // Drives the Active/Inactive pills and column. Read off the lease summaries, which are the only
  // portfolios this screen loads.
  const hasLease = hasLiveLease(accounts, portfolioApiSelectors.getSummaryStatusByPortfolioId(state))

  const identification =
    partyType === 'person' && idNumber
      ? idNumber
      : partyType === 'person' && passportNumber
      ? passportNumber
      : companyRegistration

  return {
    id,
    name,
    companyName,
    lastName,
    email,
    contactNumber,
    identification,
    createdAt,
    tags,
    partyType,
    accounts,
    hasBankDetails,
    ficaCompliance,
    hasLiveLease: hasLease,
  }
})

export const contactsFilters = createSelector(getState, state => {
  const parties = partyApiSelectors.getParties(state)
  const contacts = pipe(
    keys,
    map((id: any) => getPartyDataForContactsTable(state)(id)),
  )(parties)

  const filterContactsByPartyTag = (tag: any) =>
    contacts.filter((contact: any) => contact.tags.filter((t: any) => t.includes(tag)).length > 0)

  const filterContactsByAccountTag = (tag: any) =>
    contacts.filter((contact: any) => contact.accounts.filter((account: any) => account.tag.includes(tag)).length > 0)

  const filterContactsByPartyTagAndAccountTag = (...tags: any[]) =>
    pipe(
      map((tag: any) =>
        uniqBy((p: any) => p.id, [...filterContactsByPartyTag(tag), ...filterContactsByAccountTag(tag)]),
      ),
      flatten,
      uniqBy((p: any) => p.id),
    )(tags)

  const filterContactsByTag = (tag: any) => {
    switch (tag) {
      case 'Tenant':
      case 'Owner':
        return filterContactsByPartyTagAndAccountTag(tag)
      case 'Applicants':
        return filterContactsByPartyTagAndAccountTag('Application')
      case 'Team':
        return filterContactsByPartyTagAndAccountTag('Agent')
      case 'Beneficiaries':
        return filterContactsByPartyTagAndAccountTag(
          'Supplier',
          'Contractor',
          'Municipality',
          'Rates',
          'BodyCorporate',
          'Levies',
        )
      default:
        return []
    }
  }

  const tenants = filterContactsByTag('Tenant')
  const owners = filterContactsByTag('Owner')
  const applicants = filterContactsByTag('Applicants')
  const team = filterContactsByTag('Team')
  const beneficiaries = filterContactsByTag('Beneficiaries')
  const incompleteDetails = contacts.filter((c: any) => !partyApiSelectors.partyHasBankDetails(state)(c.id))

  return [
    {
      key: 'all',
      label: 'All',
      contacts: contacts,
      count: contacts.length,
    },
    {
      key: 'owners',
      label: 'Owners',
      contacts: owners,
      count: owners.length,
    },
    {
      key: 'tenants',
      label: 'Tenants',
      contacts: tenants,
      count: tenants.length,
    },
    {
      key: 'applicants',
      label: 'Applicants',
      contacts: applicants,
      count: applicants.length,
    },
    {
      key: 'beneficiaries',
      label: 'Beneficiaries',
      contacts: beneficiaries,
      count: beneficiaries.length,
    },
    {
      key: 'team',
      label: 'Team',
      contacts: team,
      count: team.length,
    },
  ]
})

// eslint-disable-next-line react/display-name
export const getContactInfo = createSelector(getState, state => (id: any) => {
  const party = partyApiSelectors.getPartyDetailsById(state)(id)
  const partyType = partyApiSelectors.getPartyTypeById(state)(id)
  const partyName =
    partyType === 'person'
      ? partyApiSelectors.getPartyNameById(state)(id)
      : partyApiSelectors.getPartyTradingAsOrCompanyNameById(state)(id)
  const name = partyApiSelectors.getPartyNameById(state)(id)
  const companyName = partyType === 'company' ? partyApiSelectors.getPartyCompanyNameById(state)(id) : undefined
  const email = partyApiSelectors.getPartyEmailById(state)(id)
  const contactNumber = partyApiSelectors.getPartyTelNumberById(state)(id)
  const idNumber = partyType === 'person' ? partyApiSelectors.getPersonsIdNumber(state)(id) : null
  const passportNumber = partyType === 'person' ? partyApiSelectors.getPersonsPassportNumber(state)(id) : null
  const lastModified = partyApiSelectors.getPartyUpdatedAt(state)(id)
  const taxNumber = partyApiSelectors.getPartyTaxNumberById(state)(id)
  const vatNumber = partyApiSelectors.getPartyVatNumberById(state)(id)
  const companyRegistration = partyApiSelectors.getPartyCompanyRegistration(state)(id) || null
  const bank = get(party, 'bank')
  const accountType = get(party, 'acccountType')
  const accountNumber = get(party, 'accountNumber')
  const address = partyApiSelectors.getPartyAddressById(state)(id)

  return {
    partyName,
    name,
    companyName,
    lastModified: lastModified && format(lastModified, 'dd-MM-yyyy HH:mm'),
    email,
    contactNumber,
    idNumber,
    passportNumber,
    taxNumber,
    vatNumber,
    companyRegistration,
    bank: bank && (
      <p>
        {bank} <br />
        <span>{accountType}</span>
      </p>
    ),
    accountNumber,
    address,
  }
})

export const getInitialValuesForEdit = createSelector(getState, state => (id: any) => {
  const details = partyApiSelectors.getPartyDetailsById(state)(id)
  return {
    ...details,
    passport: details?.passport ? details.passport : '',
    dateOfBirth: details?.dateOfBirth ? details.dateOfBirth : '',
    alternativeEmailAddress: details?.alternativeEmailAddress ? details.alternativeEmailAddress : '',
    buildingName: '',
    unitNumber: '',
    streetAddress: '',
    municipality: '',
    postalCode: '',
    country: '',
  }
})
